/**
 * Application-wide constants
 */

// ============================================================================
// API Constants
// ============================================================================

export const API_TIMEOUT = 30000; // 30 seconds
export const API_RETRY_ATTEMPTS = 3;
export const API_RETRY_DELAY = 1000; // 1 second

// ============================================================================
// File Upload Constants
// ============================================================================

export const ACCEPTED_FILE_TYPES = {
  PPTX: '.pptx',
} as const;

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const UPLOAD_CHUNK_SIZE = 1024 * 1024; // 1MB chunks

// ============================================================================
// UI Constants
// ============================================================================

export const SIDEBAR_WIDTH = 280;
export const NAVBAR_HEIGHT = 64;
export const FOOTER_HEIGHT = 48;

export const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2] as const;
export const DEFAULT_ZOOM = 1;

export const DEBOUNCE_DELAY = 300; // milliseconds
export const TOAST_DURATION = 3000; // milliseconds

// PowerPoint EMU (English Metric Units) to Pixels conversion
export const EMU_TO_PX = 1 / 9525;

// Image loading constants
export const IMAGE_RETRY_MAX = 3;
export const IMAGE_RETRY_DELAY = 1000; // milliseconds

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  THEME: 'pptx-annotator-theme',
  AUTH_TOKEN: 'pptx-annotator-auth-token',
  USER_PREFERENCES: 'pptx-annotator-preferences',
  RECENT_PRESENTATIONS: 'pptx-annotator-recent',
} as const;

// ============================================================================
// Route Paths
// ============================================================================

export const ROUTES = {
  HOME: '/',
  EXTRACT: '/extract',
  EDITOR: '/editor',
  EDITOR_WITH_ID: '/editor/:presentationId',
  SAVED_PRESENTATIONS: '/dashboard',
  PRICING: '/pricing',
  HOW_IT_WORKS: '/how-it-works',
  ABOUT: '/about',
  CONTACT: '/contact',
  FAQ: '/faq',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  NOT_FOUND: '*',
} as const;

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  GENERIC: 'An unexpected error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UPLOAD_FAILED: 'File upload failed. Please try again.',
  INVALID_FILE: 'Invalid file type. Please upload a .pptx file.',
  FILE_TOO_LARGE: `File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
  AUTH_REQUIRED: 'You must be logged in to access this feature.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
} as const;

// ============================================================================
// Success Messages
// ============================================================================

export const SUCCESS_MESSAGES = {
  UPLOAD_SUCCESS: 'File uploaded successfully!',
  SAVE_SUCCESS: 'Changes saved successfully!',
  DELETE_SUCCESS: 'Item deleted successfully!',
  COPY_SUCCESS: 'Copied to clipboard!',
} as const;

// ============================================================================
// Validation Constants
// ============================================================================

export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  MIN_PASSWORD_LENGTH: 8,
  MAX_NAME_LENGTH: 100,
  MAX_MESSAGE_LENGTH: 1000,
} as const;

// ============================================================================
// Feature Flags
// ============================================================================

export const FEATURES = {
  ENABLE_DARK_MODE: true,
  ENABLE_KEYBOARD_SHORTCUTS: true,
  ENABLE_ANALYTICS: false,
  ENABLE_OFFLINE_MODE: false,
} as const;
